import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Shield } from 'lucide-react';
import axios from 'axios';

export const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checkingInstance, setCheckingInstance] = useState(true);
  const [mfaStep, setMfaStep] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const { login, API } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkInstance = async () => {
      try {
        const res = await axios.get(`${API}/auth/instance-status`);
        if (!res.data.has_users) {
          navigate('/setup', { replace: true });
          return;
        }
      } catch (err) {}
      setCheckingInstance(false);
    };
    checkInstance();
  }, [API, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Defensive client-side normalization: iOS Safari auto-capitalizes
      // the first letter of input fields by default. Even with
      // autoCapitalize="none" on the <input>, password managers and
      // autofill can still inject mixed-case values. The backend is now
      // case-insensitive on email lookups, but we also normalize here so
      // the request body and any logs stay tidy.
      const normalizedEmail = (formData.email || '').trim().toLowerCase();
      const result = await login(normalizedEmail, formData.password, mfaStep ? totpCode : undefined);
      if (result?.mfa_required) {
        setMfaStep(true);
        setLoading(false);
        return;
      }
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Authentication failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0F172A' }} data-testid="login-page">
      {checkingInstance ? (
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      ) : (
      <div className="w-full max-w-md">
        <Card className="bg-white p-6 md:p-8 shadow-xl rounded-xl">
          <div className="mb-6 md:mb-8 flex flex-col items-center">
            {/* FMiQ-One Logo with dark background container */}
            <div className="bg-slate-900 rounded-xl p-4 mb-3">
              <img 
                src="/images/fmiq-one-logo.png" 
                alt="FMiQ-One" 
                className="w-full max-w-[200px]" 
              />
            </div>
            <p className="text-slate-500 text-sm md:text-base font-medium mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="login-subtitle">
              Integrated Workplace Management System
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="auth-form">
            <div className="mb-4">
              <Label htmlFor="email" className="text-slate-900 font-semibold text-sm md:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="email"
                inputMode="email"
                className="mt-1.5 bg-white border-slate-200 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Enter your email"
                data-testid="input-email"
              />
            </div>

            <div className="mb-2">
              <Label htmlFor="password" className="text-slate-900 font-semibold text-sm md:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="current-password"
                  className="bg-white border-slate-200 focus:ring-blue-500 focus:border-blue-500 text-base pr-10"
                  placeholder="Enter your password"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="mb-6 text-right">
              <Link
                to="/reset-password"
                className="text-xs md:text-sm font-medium hover:underline"
                style={{ color: '#3B82F6' }}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            {/* MFA TOTP Step */}
            {mfaStep && (
              <div className="mb-5 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="mfa-login-step">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-teal-600" />
                  <Label className="text-slate-900 font-semibold text-sm">Authentication Code</Label>
                </div>
                <p className="text-xs text-slate-500 mb-2">Enter the 6-digit code from your authenticator app</p>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center font-mono text-lg tracking-widest"
                  autoFocus
                  data-testid="mfa-totp-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full text-white py-5 md:py-6 text-sm md:text-base font-semibold"
              style={{ backgroundColor: '#3B82F6', fontFamily: 'Inter, sans-serif' }}
              data-testid="submit-button"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Sign In
            </Button>
          </form>

          {/* Invite-only notice */}
          <div className="mt-6 text-center">
            <p className="text-xs md:text-sm text-slate-500" style={{ fontFamily: 'Lato, sans-serif' }}>
              Don&apos;t have an account?{' '}
              <span className="text-slate-700 font-medium">Contact your administrator for an invitation.</span>
            </p>
          </div>
        </Card>
      </div>
      )}
    </div>
  );
};

export default LoginPage;
